import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import UpsertDoctorForm from 'components/doctors/UpsertDoctorForm';
import CheckBox from 'components/final-form/CheckBox';
import {
  DropdownOption,
  mapToDropdownOptionArrayWithTextGetter,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import { selectInput } from 'components/final-form/SelectInput';
import TextArea from 'components/final-form/TextArea';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import ToolbarButton from 'components/layout/ToolbarButton';
import LoaderComponent from 'components/LoaderComponent';
import { MandateConsumerRenderProps, withMandateContext } from 'components/MandateContext';
import {
  PatientCentralConsumerRenderProps,
  withPatientCentralContext
} from 'components/patient-entry/PatientCentralContext';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import { previousDosesOptions } from 'components/patient-entry/vaccinationUtils';
import { VaccinationError } from 'components/patient/PatientInfo';
import { CardData, CardReaderState } from 'components/patient/upsert/PatientCardData';
import UpsertPatient from 'components/patient/upsert/UpsertPatient';
import { FormState } from 'final-form';
import createPmedDecorator from 'final-form-calculate-pmed/decorator';
import _ from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Icon, Message, Modal, Popup } from 'semantic-ui-react';
import { getAllCountries } from 'services/countryServices';
import { findDoctorById, searchDoctorsForInput } from 'services/doctorServices';
import axios from 'services/http';
import { getInterventionAvailabilityForInterventionBooking } from 'services/interventionBookingServices';
import { getInterventionPrice } from 'services/interventionPriceServices';
import { getAllLanguages } from 'services/languageServices';
import { getAllVaccinationMedicaments } from 'services/medicamentServices';
import { getVaccinationInProgressForBooking } from 'services/patientRecordServices';
import {
  checkPatientHealthCardData,
  findPatientById,
  getPatientData,
  updatePatientDataFromHealthCard
} from 'services/patientServices';
import { fetchAllProducts } from 'services/productServices';
import styled from 'styled-components';
import {
  InterventionBookingStatus,
  InterventionExternalCode,
  PaymentMethod,
  TestResultStatus,
  Urgency
} from 'ts-types/api.enums';
import {
  CompleteVaccinationBookingRequestDto,
  CountryDto,
  DoctorDto,
  HealthCardDataDto,
  InterventionAvailabilityDetailsDto,
  InterventionBookingSearchResult,
  InterventionDto,
  LanguageDto,
  MedicamentDto,
  PatientDataResponseDto,
  PatientDto,
  ProductDto,
  UpsertDoctorDto,
  UpsertPatientDto,
  VaccinationDto
} from 'ts-types/api.types';
import { ChromeExtensionStatus, getChromeExtensionStatus, readHealthCard } from 'utils/chomeExtensionUtils';
import { isoToDisplayDate, toIsoDateString } from 'utils/dateUtils';
import { noop } from 'utils/functionUtils';
import { IndexSignature } from 'utils/praxTypes';
import { ccdValidator, required, twintTransactionIdValidator } from 'utils/validatorUtils';


const CheckInOutDiv = styled.div`

  min-width: 35rem;

  .header {
    margin-bottom: 0.7rem;
    padding: 0.5rem 0.65rem 1rem;
    font-size: 1.2rem;
    font-weight: bold;
  }

  .form-content {
    min-height: 17rem;

    label {
      font-weight: bold;
    }
  }

  .actions-row {

    button {
      display: inline-block;
      margin-right: 0.5rem;
    }
  }

  .submit-error {
    color: darkred;
    display: inline-block;
  }

  .submit-warn, .doctor-email-warn {
    color: #8a7305;
    display: inline-block;
  }

  .doctor-email-warn {
    margin-top: .2rem;
  }

  .patient-data-container {
    i.icon {
      margin-left: .5rem;
      cursor: pointer;
    }
  }

  .different-data {
    color: #794b02;
  }

  .ui.compact.button.update-btn {
    margin-left: 8rem;
  }

  & .doctor-email-warning {
    color: #8a7305;
    margin-top: .2rem;
  }
`;

const StyledIcon = styled.span`
  float: right;
`;

const InfoDiv = styled.div`
  & .info-message {
    margin-top: 2rem;
    min-height: 105px;
  }

  & .info-message .download-link {
    margin-top: 1.5rem;
  }

  & .actions .button:first-child {
    margin-left: 0;
  }
`;

const PopupErrorMessage = styled(Message)`
  min-width: 27rem;
`;

const LoaderPxGrid = styled(PxGrid)`
  height: 300px;
`;

const RapidTestCodeRowStyles = {
  marginBottom: "1rem",
  borderBottom: "1px solid rgba(34,36,38,.15)",
  paddingBottom: "1.5rem"
}

interface CheckHealthCardData {
  name: string;
  firstName: string;
  birthDate: string;
}

interface CheckoutFormValues {
  interventionId: number;
  referralDoctorId?: number;
  medicamentId: number;
  lotNumber: string;
  previousDoses?: number;
  productId: string;
  inkasso: PaymentMethod;
  creditCardDigits?: string;
  twintTransactionId?: string;
  urgency: Urgency;
  generateCertificate: boolean;
  generateReport: boolean;
  remarks?: string;
}

interface Props extends MessageSourceProps,
    AuthConsumerRenderProps,
    PatientCentralRefDataConsumerRenderProps,
    PatientCentralConsumerRenderProps,
    MandateConsumerRenderProps {

  interventionMap: { [id: number]: InterventionDto },
  bookingInTransition: InterventionBookingSearchResult,
  onCheckInOutSave: (bookingId: number,
                     status: InterventionBookingStatus | undefined,
                     request: CompleteVaccinationBookingRequestDto | undefined) => void
  onCancel: () => void
}

export type TransitionMode = "checkIn" | "checkOut";

interface State {
  transitionMode: TransitionMode;

  requiredDataLoaded: boolean;
  patientData?: PatientDataResponseDto;
  medicaments: MedicamentDto[];
  medicamentsOptions: DropdownOption[];
  interventionOptions: DropdownOption[];
  previousDoseOptions: DropdownOption[];
  productOptions: DropdownOption[];
  urgencyOptions: DropdownOption[];

  patientId?: number,
  patient?: UpsertPatientDto,
  patientModalOpen: boolean,
  patientLoading: boolean,

  countries: Array<CountryDto>,
  languages: Array<LanguageDto>,

  referralDoctorMap: IndexSignature,
  referralDoctorOptions: DropdownOption[],

  healthCardPopupOpen: boolean;
  healthCardPopupStatus: CardReaderState;
  updateSuccess: boolean;

  healthCardCheck: boolean;
  checkHealthCardData?: CheckHealthCardData;

  vaccinationError?: VaccinationError;

  generatingCertificates: boolean;

  healthCardRead: boolean;
  submitError?: string;
  submitWarning?: string;

  missingDoctorEmail: boolean;
  doctor?: UpsertDoctorDto,
  doctorId?: number,
  editDoctorModalOpen: boolean;

  productId?: string,
  products: ProductDto[],
  interventionPriceInsurance?: number;
  interventionPricePatient?: number;
  paymentMethodOptions: DropdownOption[];
}

class VaccinationTransitionForm extends Component<Props, State> {

  cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  initialValues: Partial<CheckoutFormValues> = {};

  productMap: { [id: string]: ProductDto[] } = {};

  constructor(props: Props) {
    super(props);

    const {getMessage} = props;

    const transitionMode = props.bookingInTransition.status === InterventionBookingStatus.PLANNED
        ? "checkIn" : "checkOut";

    const previousDoseOptions = previousDosesOptions(getMessage);

    const urgencyOptions = stringArrayToDropdownOptionArray(
        Object.values(Urgency), getMessage, "enum.urgency");

    const paymentMethodOptions = stringArrayToDropdownOptionArray(
        Object.values(PaymentMethod), getMessage, "enum.paymentMethod");


    this.state = {
      transitionMode,

      requiredDataLoaded: false,
      medicaments: [],
      medicamentsOptions: [],
      interventionOptions: [],
      previousDoseOptions,
      productOptions: [],
      urgencyOptions: urgencyOptions,
      products: [],
      paymentMethodOptions,

      referralDoctorMap: [],
      referralDoctorOptions: [],

      healthCardPopupOpen: false,
      updateSuccess: false,
      healthCardCheck: true,
      healthCardPopupStatus: CardReaderState.INITIAL,

      healthCardRead: false,

      patientModalOpen: false,
      patientLoading: false,

      generatingCertificates: false,

      languages: [],
      countries: [],

      missingDoctorEmail: false,
      editDoctorModalOpen: false
    };
  }

  componentDidMount() {
    this.loadRequiredData();
  }

  loadRequiredData = async () => {
    const booking = this.props.bookingInTransition;
    const {interventionMap, interventions} = this.props;
    const {transitionMode} = this.state;

    try {

      const patientData = await getPatientData(booking.patientId, this.cancelTokenSource);

      const medicaments = await getAllVaccinationMedicaments(this.cancelTokenSource);
      const medicamentsOptions = mapToDropdownOptionArrayWithTextGetter(
          medicaments, medicamant => `${medicamant.code} - ${medicamant.description}`, "id");

      let vaccinationInterventions: InterventionDto[] = [];
      let interventionOptions: DropdownOption[] = [];
      let products: ProductDto[] = [];

      try {
        let data: InterventionAvailabilityDetailsDto | VaccinationDto | undefined = undefined;
        let vaccinationData = data as VaccinationDto | undefined;
        let interventionId = booking.interventionId;

        if (transitionMode === 'checkIn') {

          data = await getVaccinationInProgressForBooking(booking.bookingId, this.cancelTokenSource);
          if (!data) {
            data = await getInterventionAvailabilityForInterventionBooking(
                booking.bookingId, this.cancelTokenSource);
          }
          vaccinationData = data as VaccinationDto;

        } else {
          data = await getVaccinationInProgressForBooking(booking.bookingId, this.cancelTokenSource);
          vaccinationData = data as VaccinationDto;
        }

        let allProducts = await fetchAllProducts(false, this.cancelTokenSource);
        this.productMap = _.groupBy(allProducts, "externalCode");

        products = allProducts.filter(product =>
            (product.active && !product.systemProduct)
            || (vaccinationData && product.id === vaccinationData.productId));

        this.initialValues = {
          ...this.initialValues,
          creditCardDigits: vaccinationData ? vaccinationData.creditCardDigits : undefined,
          twintTransactionId: vaccinationData ? vaccinationData.twintTransactionId : undefined,
          remarks: vaccinationData ? vaccinationData.remarks : undefined
        }

        const selectedVaccination = interventionMap[interventionId];

        if (selectedVaccination.externalCode === InterventionExternalCode.VACCINATION_BOOSTER
            || selectedVaccination.externalCode === InterventionExternalCode.VACCINATION_SINGLE_DOSE) {

          vaccinationInterventions = interventions
              .filter(i => i.externalCode === InterventionExternalCode.VACCINATION_BOOSTER
                  || i.externalCode === InterventionExternalCode.VACCINATION_SINGLE_DOSE);
        }

        interventionOptions = this.mapVaccinationInterventionOptions(vaccinationInterventions);

        if (data.referralDoctorId) {
          await this.fetchSpecificReferralDoctor(data.referralDoctorId);
        }

        this.initialValues = {
          interventionId: selectedVaccination.id,
          referralDoctorId: data.referralDoctorId,
          medicamentId: data.medicamentId,
          lotNumber: data.lotNumber
        }

        if (vaccinationData) {
          const productId = vaccinationData.productId;
          const product = productId ? products.find(product => product.id === productId) : undefined;

          this.initialValues.productId = productId;

          if (vaccinationData.urgency) {
            this.initialValues.urgency = Urgency[vaccinationData.urgency];
          } else if (product && product.urgency) {
            this.initialValues.urgency = Urgency[product.urgency];
          }

          if (vaccinationData.inkasso) {
            this.initialValues.inkasso = PaymentMethod[vaccinationData.inkasso];
          }

          if (vaccinationData.creditCardDigits) {
            this.initialValues.creditCardDigits = vaccinationData.creditCardDigits;
          }

          if (vaccinationData.twintTransactionId) {
            this.initialValues.twintTransactionId = vaccinationData.twintTransactionId;
          }

          if (vaccinationData.remarks) {
            this.initialValues.remarks = vaccinationData.remarks;
          }

          this.initialValues.generateCertificate = product ? product.generateCertificate : false;
          this.initialValues.generateReport = product ? product.generateReport : false;

          let externalCode = InterventionExternalCode[vaccinationData!.externalCode];
          const productSearchExternalCode = this.getExternalCodeForBilling(externalCode);

          products = (this.productMap[productSearchExternalCode] || []).filter(product =>
              (product.active && !product.systemProduct)
              || (vaccinationData && product.id === vaccinationData.productId));
        }

        const lang = this.props.language;
        const productOptions = mapToDropdownOptionArrayWithTextGetter(
            products, product => this.getProductDescription(product, lang));

        if (_.has(data, "previousDoses")) {
          this.initialValues.previousDoses = (data as VaccinationDto).previousDoses;
        }

        this.setState({
          productOptions
        })
      } catch (e) {
        // do nothing
      }

      let languages: LanguageDto[] = [];
      let countries: CountryDto[] = [];
      if (this.state.transitionMode === "checkIn") {
        languages = await getAllLanguages(this.cancelTokenSource);
        countries = await getAllCountries(this.cancelTokenSource);
      }

      this.setState({
        patientData,
        medicaments,
        medicamentsOptions,
        interventionOptions,
        languages,
        countries,
        products
      });

    } finally {
      this.setState({
        requiredDataLoaded: true
      });
    }
  };

  getExternalCodeForBilling = (externalCode: InterventionExternalCode): InterventionExternalCode => {
    const replacementInterventionForBilling: { [key: string]: InterventionExternalCode } = {
      [InterventionExternalCode.VACCINATION_DOSE_2]: InterventionExternalCode.VACCINATION_DOSE_1,
      [InterventionExternalCode.VACCINATION_CHILD_DOSE_2]: InterventionExternalCode.VACCINATION_CHILD_DOSE_1
    };

    const externalCodeForBilling: InterventionExternalCode =
        replacementInterventionForBilling[externalCode] || externalCode;

    return externalCodeForBilling;
  }

  getProductDescription = (product: ProductDto, lang: string) => {
    if (lang === 'en') {
      return product.descriptionEn || product.description;
    } else if (lang === 'fr') {
      return product.descriptionFr || product.description;
    } else if (lang === 'it') {
      return product.descriptionIt || product.description;
    }
    return product.description;
  }

  fetchSpecificReferralDoctor = async (referralDoctorId: number) => {
    try {
      const referralDoctor = await findDoctorById(referralDoctorId, this.cancelTokenSource);

      const doctorName = referralDoctor.name;
      const doctorFirstName = referralDoctor.firstName ? referralDoctor.firstName : "";
      const doctorCity = referralDoctor.city;
      const doctorStreet = referralDoctor.street ? referralDoctor.street : "";

      const referralDoctorOption: DropdownOption = {
        key: referralDoctorId,
        text: `${doctorName}, ${doctorFirstName}, ${doctorCity}, ${doctorStreet}`,
        value: referralDoctorId
      };

      const referralDoctorDto: DoctorDto = {
        ...referralDoctor,
        id: referralDoctorId
      };

      this.setState({
        referralDoctorMap: _.keyBy([referralDoctorDto], "id"),
        referralDoctorOptions: [referralDoctorOption],
        missingDoctorEmail: !referralDoctor.email
      });
    } catch (e) {

    }

  };

  parseCreditCardDigitsInput = (value: any) => {
    if (!value || value.length < 5) {
      return value;
    } else {
      return value.slice(0, 4);
    }
  };

  parseTwintTransactionIdDigitsInput = (value: any) => {
    if (!value || value.length < 17) {
      return value;
    } else {
      return value.slice(0, 16);
    }
  };


  searchDoctors = (search: string): Promise<DoctorDto[]> => {
    return searchDoctorsForInput(search, this.cancelTokenSource, 75);
  };

  mapDoctors = (doctors: DoctorDto[]): DropdownOption[] => {

    this.setState({
      referralDoctorMap: _.keyBy(doctors, "id")
    });

    return doctors.map(doctor => ({
      key: doctor.id,
      text: `${doctor.name}, ${doctor.firstName ? doctor.firstName : ""}, ${doctor.city}, ${doctor.street ? doctor.street : ""}`,
      value: doctor.id
    }));
  };

  mapVaccinationInterventionOptions = (vaccinationInterventions: InterventionDto[]) => {
    const {language} = this.props;
    return mapToDropdownOptionArrayWithTextGetter(vaccinationInterventions, intervention => {
      const descMap: { [lang: string]: string } = {
        "de": intervention.description,
        "en": intervention.descriptionEn || intervention.description,
        "fr": intervention.descriptionFr || intervention.description,
        "it": intervention.descriptionIt || intervention.description
      };
      return descMap[language]
    });
  }

  triggerUpdatePatientFromHealthCard = async (patientId: number) => {
    const {healthCardCheck} = this.state;
    const {config} = this.props;

    this.setState({
      healthCardPopupOpen: true,
      healthCardPopupStatus: CardReaderState.INITIAL
    });

    const extStatus = await getChromeExtensionStatus(config);

    if (ChromeExtensionStatus.NO_EXTENSION === extStatus) {
      this.setHealthCardReaderStatus(CardReaderState.NO_EXTENSION);
    } else if (ChromeExtensionStatus.OUTDATED_EXTENSION === extStatus) {
      this.setHealthCardReaderStatus(CardReaderState.OUTDATED_EXTENSION);
    } else if (ChromeExtensionStatus.NO_HOST_APPLICATION === extStatus) {
      this.setHealthCardReaderStatus(CardReaderState.NO_HOST_APPLICATION);
    } else if (ChromeExtensionStatus.OUTDATED_HOST_APP === extStatus) {
      this.setHealthCardReaderStatus(CardReaderState.OUTDATED_HOST_APP);
    } else if (ChromeExtensionStatus.OK === extStatus) {

      const cardReaderResponse: any = await readHealthCard();

      const payload = cardReaderResponse.payload;
      if (cardReaderResponse.status === 'ERROR' && payload.errorCode === "NO_PATIENT_DATA") {
        this.setHealthCardReaderStatus(CardReaderState.NO_CARD);
      } else if (cardReaderResponse.status === 'ERROR') {
        this.setHealthCardReaderStatus(CardReaderState.ERROR);
      } else if (cardReaderResponse.status === 'RESULT') {
        const responseCardData = payload;

        const cardData: CardData = {
          name: responseCardData.lastName,
          firstName: responseCardData.firstName,
          birthDate: responseCardData.dateOfBirth,
          ahvNumber: responseCardData.insuranceNumber,
          sex: responseCardData.gender === "1" ? 1 : 0,
          country: responseCardData.issuingState,
          guarantorName: responseCardData.nameOfTheInstitution,
          institutionId: responseCardData.identificationNumberOfTheInstitution,
          cardId: responseCardData.insuredPersonNumber,
          expiryDate: responseCardData.expiryDate
        };

        const request: HealthCardDataDto = {
          patientId: patientId,
          name: cardData.name,
          firstName: cardData.firstName,
          bagNumber: cardData.institutionId,
          ahvNumber: cardData.ahvNumber,
          healthCardNumber: cardData.cardId,
          dateOfBirth: cardData.birthDate,
          expiryDate: cardData.expiryDate,
          gender: responseCardData.gender,
          guarantorName: responseCardData.guarantorName
        };

        let check = true;

        if (healthCardCheck) {
          check = await checkPatientHealthCardData(request, this.cancelTokenSource);
          this.setState({
            healthCardCheck: check
          });
        }

        if (!check) {
          this.setState({
            checkHealthCardData: {
              name: request.name,
              firstName: request.firstName,
              birthDate: request.dateOfBirth
            },
            healthCardPopupOpen: false
          })
        }

        if (check) {

          const patientData = await updatePatientDataFromHealthCard(request, this.cancelTokenSource);

          if (patientData) {

            this.setState({
              patientData: patientData,
              updateSuccess: true
            });
          }

          this.setState({
            healthCardPopupOpen: false,
            healthCardCheck: true,
            checkHealthCardData: undefined
          });
        }
      }
    }
  }

  setHealthCardReaderStatus(status: CardReaderState) {
    this.setState({
      healthCardPopupStatus: status
    });
  }

  closeUpdateFromHealthCardPopup = () => {
    this.setState({
      healthCardPopupOpen: false
    });
  }

  handleSubmit = (values: Partial<CheckoutFormValues>) => {
    const {healthCardRead} = this.state;

    // const foundIntervention = interventions.find(i => i.id === bookingInTransition.interventionId);
    // const dose2 = foundIntervention && foundIntervention.externalCode === InterventionExternalCode.VACCINATION_DOSE_2;
    //
    // if (transitionMode === "checkOut" && dose2 && !vaccinationCertificatePrinted) {
    //   this.setState({
    //     submitError: "patientCentral.checkOut.error.vaccinationCertificateNotPrinted"
    //   });
    //   return;
    // }

    // if (transitionMode === "checkOut" && !vaccinationCardPrinted) {
    //   this.setState({
    //     submitError: "patientCentral.checkOut.error.vaccinationCardNotPrinted"
    //   });
    //   return;
    // }

    if (!healthCardRead) {
      this.setState({
        healthCardRead: true,
        submitWarning: "patientCentral.checkOut.error.healthCardNotRead"
      });
      return;
    }

    this.setState({
      generatingCertificates: true
    });

    this.onSave(values);
  }

  onFormChange = (formState: FormState) => {

    const values = formState.values as CheckoutFormValues;

    const productIdState = this.state.productId;
    const productId = values.productId;
    if (productId && productId !== productIdState) {
      this.setState({
        interventionPricePatient: undefined,
        interventionPriceInsurance: undefined,
        productId: productId
      });
      getInterventionPrice(productId, toIsoDateString(moment()), this.cancelTokenSource)
          .then(result => {
            const privateTariffInvoices = result.invoices.filter(inv => inv.privateTariff);
            if (privateTariffInvoices) {
              const totalAmountPrivateTariff = privateTariffInvoices.reduce(
                  (total, invoice) => total + invoice.amount, 0);

              this.setState({interventionPricePatient: totalAmountPrivateTariff});
            }

            const insuranceInvoices = result.invoices.filter(inv => !inv.privateTariff);
            if (insuranceInvoices) {
              const totalAmountInsurance = insuranceInvoices.reduce(
                  (total, invoice) => total + invoice.amount, 0);

              this.setState({interventionPriceInsurance: totalAmountInsurance});
            }
          })
          .catch(noop);
    }
  }

  onSave = (values: Partial<CheckoutFormValues>) => {
    const {bookingInTransition, options, onCheckInOutSave} = this.props;
    const {transitionMode, interventionPricePatient} = this.state;

    const bookingId = bookingInTransition.bookingId;

    const request: CompleteVaccinationBookingRequestDto = {
      interventionId: values.interventionId!,
      referralDoctorId: values.referralDoctorId,
      medicamentId: values.medicamentId!,
      lotNumber: values.lotNumber!,
      previousDoses: values.previousDoses,
      status: transitionMode === "checkIn" ? TestResultStatus.IN_PROGRESS : TestResultStatus.VALID,
      productId: values.productId,
      cashAmount: interventionPricePatient,
      inkasso: values.inkasso!,
      creditCardDigits: values.creditCardDigits,
      twintTransactionId: values.twintTransactionId,
      urgency: values.urgency!,
      generateCertificate: values.generateCertificate!,
      generateReport: values.generateReport!,
      remarks: values.remarks,
      treaterId: options.currentTreater.id
    }

    if (transitionMode === "checkIn") {
      onCheckInOutSave(bookingId, InterventionBookingStatus.AWAITING_COMPLETION, request);
    } else {
      onCheckInOutSave(bookingId, InterventionBookingStatus.COMPLETED, request);
    }
  };

  interventionPreviousDosesDecorator = createPmedDecorator({
    field: "interventionId",
    updateOnPristine: false,
    updates: {
      previousDoses: (fieldValue, allValues?: Partial<CheckoutFormValues>) => {
        const {interventionMap} = this.props;
        if (fieldValue) {
          const intervention = interventionMap[fieldValue];
          if (intervention
              && allValues
              && !allValues.previousDoses
              && InterventionExternalCode.VACCINATION_BOOSTER === intervention.externalCode) {
            return 2;
          }
        }
        return allValues!.previousDoses;
      }
    }
  });

  productDecorator = createPmedDecorator({
    field: "productId",
    updateOnPristine: false,
    updates: (value, name, allValues?: Partial<CheckoutFormValues>) => {
      if (allValues && allValues.productId) {

        const selectedProduct = this.state.products.find(product => product.id === allValues.productId);
        if (selectedProduct) {
          const updates = this.getUpdatesForProductChange(selectedProduct, allValues.inkasso);
          return {
            ...updates
          };
        }
      }

      return {};
    }
  });

  getUpdatesForProductChange = (
      selectedProduct?: ProductDto,
      inkassoCurrentValue?: string) => {

    if (!selectedProduct) {
      return {
        ["inkasso"]: undefined,
        ["generateCertificate"]: undefined,
        ["generateReport"]: undefined,
        ["urgency"]: undefined
      };
    }

    const productItems = selectedProduct.productItems;
    const invoicePrivate = productItems.map(item => item.cashOptions).includes(true);

    let inkasso = !invoicePrivate && productItems.length
        ? PaymentMethod.INVOICE_GUARANTOR : inkassoCurrentValue;
    if (invoicePrivate && inkasso === PaymentMethod.INVOICE_GUARANTOR) {
      inkasso = undefined;
    }

    return {
      inkasso: inkasso,
      generateCertificate: selectedProduct.generateCertificate,
      generateReport: selectedProduct.generateReport,
      urgency: selectedProduct.urgency ? Urgency[selectedProduct.urgency] : undefined
    };
  }

  interventionDecorator = createPmedDecorator({
    field: "interventionId",
    updateOnPristine: false,
    updates: (value, name, allValues?: Partial<CheckoutFormValues>) => {
      const {interventionMap} = this.props;
      if (value && allValues) {
        const intervention = interventionMap[value];

        const productSearchExternalCode = this.getExternalCodeForBilling(
            InterventionExternalCode[intervention.externalCode]);

        const interventionProducts: ProductDto[] = this.productMap[productSearchExternalCode] || [];

        const products: ProductDto[] = interventionProducts.filter(product =>
            (product.active && !product.systemProduct));

        const lang = this.props.language;
        const productOptions = mapToDropdownOptionArrayWithTextGetter(
            products, product => this.getProductDescription(product, lang));
        this.setState({
          products,
          productOptions
        })

        if (intervention
            && interventionProducts
            && interventionProducts.length === 1) {

          let newProductId = interventionProducts[0].id;

          const selectedProduct = interventionProducts.find(product => product.id === newProductId);
          let updates = this.getUpdatesForProductChange(selectedProduct, allValues.inkasso);

          return {
            ...updates,
            productId: newProductId
          };
        }
      }

      const updates = this.getUpdatesForProductChange(undefined, undefined);
      return {
        ...updates,
        productId: undefined
      };
    }
  });

  render = () => {

    const {
      requiredDataLoaded,
      generatingCertificates,
      transitionMode
    } = this.state;

    const {getMessage} = this.props;

    if (!requiredDataLoaded || generatingCertificates) {
      return <LoaderPxGrid>
        <Grid.Row>
          <Grid.Column width={16} textAlign="center">
            <LoaderComponent size="large"
                             message={!requiredDataLoaded
                                 ? getMessage("loading")
                                 : transitionMode === "checkIn"
                                     ? getMessage("patientCentral.checkIn.generatingCard")
                                     : getMessage("patientCentral.checkIn.generatingCertificate")}
            />
          </Grid.Column>
        </Grid.Row>
      </LoaderPxGrid>
    }

    return <React.Fragment>
      <FinalForm
          onSubmit={this.handleSubmit}
          initialValues={this.initialValues}
          decorators={[
            this.interventionPreviousDosesDecorator,
            this.productDecorator,
            this.interventionDecorator]}
          subscription={{pristine: true, submitting: true, values: true}}
          render={this.renderForm}
      />
      {this.renderUpsertPatient()}
      {this.renderUpsertDoctorModal()}
    </React.Fragment>
  }

  renderForm = ({handleSubmit, submitting, values, form}: FormRenderProps): React.ReactNode => {

    const {bookingInTransition, interventionMap, getMessage} = this.props;
    const {
      referralDoctorOptions,
      productOptions,
      referralDoctorMap,
      transitionMode,
      healthCardPopupOpen,
      checkHealthCardData,
      healthCardCheck,
      updateSuccess,
      products,
      interventionPriceInsurance,
      interventionPricePatient,
      paymentMethodOptions,
      urgencyOptions
    } = this.state;

    const patientData = this.state.patientData!;

    const inputChannel = bookingInTransition.inputChannel;
    const inputChannelDisplay = getMessage(`enum.interventionBookingInputChannel.${inputChannel}`);

    const isCheckIn = transitionMode === 'checkIn';

    const intervention = interventionMap[bookingInTransition.interventionId];
    const showIntervention = intervention
        && (intervention.externalCode === InterventionExternalCode.VACCINATION_BOOSTER
            || intervention.externalCode === InterventionExternalCode.VACCINATION_SINGLE_DOSE);

    // const foundIntervention = interventions.find(i => i.id === bookingInTransition.interventionId);
    // const dose2 = foundIntervention && foundIntervention.externalCode === InterventionExternalCode.VACCINATION_DOSE_2;

    const selectedIntervention = values.interventionId && interventionMap[values.interventionId];
    const isBoosterVaccine = selectedIntervention &&
        selectedIntervention.externalCode === InterventionExternalCode.VACCINATION_BOOSTER;

    const selectMap: IndexSignature = {
      referralDoctorId: referralDoctorOptions
    }

    const referralDoctorField = selectInput(
        {
          name: 'referralDoctorId',
          input: {
            value: form.getState().values.referralDoctorId,
            onChange: (value: string) => {
              let referralDoctorId = null;
              let referralDoctor: DoctorDto | undefined = undefined;
              if (_.isNumber(value)) {
                referralDoctorId = parseInt(value);
                if (referralDoctorMap[referralDoctorId]) {
                  referralDoctor = referralDoctorMap[referralDoctorId];
                }
              }
              form.change("referralDoctorId", referralDoctorId);
              this.setState({
                missingDoctorEmail: !!(referralDoctor && !referralDoctor.email)
              });
            }
          },
          remoteSearch: true,
          remoteSearchMethod: this.searchDoctors,
          searchResultToOptionMapper: this.mapDoctors,
          clearable: true,
          options: referralDoctorOptions
        },
        selectMap
    );

    const showCreditCardDigits = values && values.inkasso === PaymentMethod.CREDIT_CARD;
    const showTwintTransactionId = values && values.inkasso === PaymentMethod.TWINT;

    const inkassoWarning = values.inkasso === PaymentMethod.CASH || values.inkasso === PaymentMethod.INVOICE_PATIENT;
    const disabledInkasso = !values.productId && products.length;

    const foundProduct = values.productId ? products.find(product => product.id === values.productId) : undefined;
    let invoicePrivate: boolean | undefined = undefined
    if (foundProduct) {
      invoicePrivate = foundProduct.productItems.map(item => item.cashOptions).includes(true);
    }

    const freeProduct = foundProduct ? foundProduct.freeProduct : undefined;

    let filteredPaymentMethods: DropdownOption[] = paymentMethodOptions;
    if (invoicePrivate && foundProduct && foundProduct.productItems.length) {
      filteredPaymentMethods = paymentMethodOptions.filter(pm => pm.value !== PaymentMethod.INVOICE_GUARANTOR);
    } else if (!invoicePrivate && foundProduct && foundProduct.productItems.length) {
      filteredPaymentMethods = paymentMethodOptions.filter(pm => pm.value === PaymentMethod.INVOICE_GUARANTOR);
    }

    const validator = (value: string) => required(value);

    return <CheckInOutDiv id="checkInOutContent">
      <div className="header">
        {
            isCheckIn &&
            <>
              <StyledIcon>
                <Popup
                    trigger={
                      <ToolbarButton iconName="search"
                                     iconSize="large"
                                     onClick={() => this.triggerUpdatePatientFromHealthCard(bookingInTransition.patientId)}
                                     customTrigger={(
                                         <Icon.Group size="large">
                                           <Icon name="id card outline" color="grey"/>
                                         </Icon.Group>
                                     )}
                                     dataTooltip={getMessage('patient.toolbar.searchFromHealthCard')}
                                     disabled={!healthCardCheck}
                      />
                    }
                    open={healthCardPopupOpen}
                    popperDependencies={[!!healthCardPopupOpen]}
                    popperModifiers={{
                      preventOverflow: {
                        boundary: document.getElementById("checkInOutContent")
                      }
                    }}
                    content={this.renderUpdateFromHealthCardPopupContent()}
                    on="click"
                    onOpen={() => {
                    }}
                    onClose={() => this.closeUpdateFromHealthCardPopup()}
                    size="small"
                    position="bottom center"
                />
              </StyledIcon>
            </>
        }
      </div>

      <div className="form-content">
        <PxGrid>
          {
              updateSuccess &&
              <Grid.Row>
                <Grid.Column width={16}>
                  <Message success onDismiss={() => this.setState({updateSuccess: false})}>
                    <p>
                      {getMessage("patientCentral.checkIn.updateSuccess")}
                    </p>
                  </Message>
                </Grid.Column>
              </Grid.Row>
          }
          {
              !healthCardCheck &&
              <Grid.Row>
                <Grid.Column width={16}>
                  <Message warning>
                    <p>
                      {getMessage("patientCentral.checkIn.dataNotMatch")}
                      <Button
                          type="button"
                          className="update-btn"
                          compact
                          onClick={() => this.triggerUpdatePatientFromHealthCard(bookingInTransition.patientId)}
                      >
                        {getMessage("patientCentral.checkIn.updatePatientData")}
                      </Button>
                    </p>
                  </Message>
                </Grid.Column>
              </Grid.Row>
          }
          <Grid.Row>
            <Grid.Column width={3}>
              <b>{getMessage("patient")}:</b>
            </Grid.Column>
            <Grid.Column width={5}>
              <div className={"patient-data-container"}>
                <span>{patientData.firstName} {patientData.name}</span>
                {this.state.transitionMode === "checkIn" &&
                    <Icon name={'edit'} color={'grey'}
                          onClick={() => this.openPatientModal(bookingInTransition.patientId)}/>
                }
              </div>
              {
                  !healthCardCheck && checkHealthCardData !== undefined &&
                  <div className="different-data">
                    {checkHealthCardData.firstName} {checkHealthCardData.name}
                  </div>
              }
              <div>{isoToDisplayDate(patientData.birthDate)} ({patientData.patientAge})</div>
              {
                  !healthCardCheck && checkHealthCardData !== undefined &&
                  <div className="different-data">
                    {isoToDisplayDate(checkHealthCardData.birthDate)}
                  </div>
              }
            </Grid.Column>
            <Grid.Column width={3}>
              <b>{getMessage("insurance")}:</b>
            </Grid.Column>
            <Grid.Column width={5}>
              <div>{patientData.guarantorName ? patientData.guarantorName : ""}</div>
              <div>{patientData.guarantorCity ? patientData.guarantorCity : ""}</div>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              <b>{getMessage("patientCentral.overview.search.inputChannel")}:</b>
            </Grid.Column>
            <Grid.Column width={5}>
              {inputChannelDisplay}
            </Grid.Column>
            <Grid.Column width={3}>
              <b>{getMessage("patientCentral.checkIn.ahvNumber")}:</b>
            </Grid.Column>
            <Grid.Column width={5}>
              {patientData.ahvNumber}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
            </Grid.Column>
            <Grid.Column width={3}>
              <b>{getMessage("address")}:</b>
            </Grid.Column>
            <Grid.Column width={5}>
              <div>{patientData.street ? patientData.street : ""}</div>
              <div>
                {patientData.zip ? `${patientData.zip},` : ""} {patientData.city ? patientData.city : ""}
              </div>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              <b>{getMessage("anamnesis.form.treatment.referralDoctorId")}</b>
            </Grid.Column>
            <Grid.Column width={8}>
              <Field fluid {...referralDoctorField} />
              {this.state.missingDoctorEmail &&
                  <div className="doctor-email-warning">
                    <Icon name="warning sign"/>
                    {getMessage("anamnesis.form.missingDoctorEmail")}
                  </div>
              }
            </Grid.Column>
            {values.referralDoctorId &&
                <Grid.Column width={2}>
                  <Button type="button"
                          primary
                          size="mini"
                          onClick={() => this.openEditDoctorModal(values.referralDoctorId)}
                  >
                    {getMessage("msgEdit")}
                  </Button>
                </Grid.Column>
            }
          </Grid.Row>
          {showIntervention && <Grid.Row>
            <Grid.Column width={3}>
              <label htmlFor="interventionId">{getMessage("patientCentral.checkOut.form.interventionId")}</label>
            </Grid.Column>
            <Grid.Column width={8}>
              <Field
                  id="interventionId"
                  name="interventionId"
                  component={Select}
                  options={this.state.interventionOptions}
                  validate={validator}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>}
          {isBoosterVaccine &&
              <Grid.Row>
                <Grid.Column width={3}>
                  <label htmlFor="interventionId">
                    {getMessage("patientCentral.checkOut.form.previousDoses")}
                  </label>
                </Grid.Column>
                <Grid.Column width={8}>
                  <Field
                      id="previousDoses"
                      name="previousDoses"
                      component={Select}
                      options={this.state.previousDoseOptions}
                      validate={validator}
                      fluid
                  />
                </Grid.Column>
              </Grid.Row>
          }
          <Grid.Row>
            <Grid.Column width={3}>
              <label htmlFor="medicamentId">{getMessage("patientCentral.checkOut.form.medicamentId")}</label>
            </Grid.Column>
            <Grid.Column width={8}>
              <Field
                  id="medicamentId"
                  name="medicamentId"
                  component={Select}
                  options={this.state.medicamentsOptions}
                  validate={validator}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              <label htmlFor="lotNumber">{getMessage("patientCentral.checkOut.form.lotNumber")}</label>
            </Grid.Column>
            <Grid.Column width={8}>
              <Field
                  id="lotNumber"
                  name="lotNumber"
                  component={Input}
                  validate={validator}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              <label htmlFor="productId">{getMessage("anamnesis.form.productId")}: </label>
            </Grid.Column>
            <Grid.Column width={8}>
              <Field
                  id="productId"
                  name="productId"
                  component={Select}
                  options={productOptions}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>

          {
              freeProduct &&
              <Grid.Row>
                <Grid.Column>
                  <span style={{fontStyle: "italic", color: "#0096FF"}}>
                    {getMessage("product.freeProduct.message")}
                  </span>
                </Grid.Column>
              </Grid.Row>
          }

          {
              !freeProduct &&
              <>
                <Grid.Row>
                  <Grid.Column width={3}>
                    <label htmlFor="amountInsurance">{getMessage("anamnesis.form.amountInsurance")}:</label>
                  </Grid.Column>
                  <Grid.Column width={2}>
                    {
                        interventionPriceInsurance !== undefined &&
                        <span>{interventionPriceInsurance}&nbsp;CHF</span>
                    }
                  </Grid.Column>

                  <Grid.Column width={3}>
                    <label htmlFor="amountPatient">{getMessage("anamnesis.form.amountPatient")}:</label>
                  </Grid.Column>
                  <Grid.Column width={2}>
                    {
                        interventionPriceInsurance !== undefined &&
                        <span>{interventionPricePatient}&nbsp;CHF</span>
                    }
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row style={{...RapidTestCodeRowStyles}}>
                  <Grid.Column width={3}>
                    <label htmlFor="paymentMethod">{getMessage("patientCentral.checkIn.paymentMethod")}:</label>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Field
                        id="inkasso"
                        name="inkasso"
                        component={Select}
                        validate={required}
                        options={filteredPaymentMethods}
                        fluid
                        disabled={disabledInkasso}
                    />
                  </Grid.Column>
                  {
                      showCreditCardDigits &&
                      <Grid.Column width={2}>
                        <Field
                            id="creditCardDigits"
                            name="creditCardDigits"
                            component={Input}
                            placeholder="####"
                            validate={ccdValidator}
                            parse={this.parseCreditCardDigitsInput}
                            fluid
                        />
                      </Grid.Column>
                  }
                  {
                      showTwintTransactionId &&
                      <Grid.Column width={4}>
                        <Field
                            id="twintTransactionId"
                            name="twintTransactionId"
                            component={Input}
                            placeholder="000022A2555H7777"
                            validate={twintTransactionIdValidator}
                            parse={this.parseTwintTransactionIdDigitsInput}
                            fluid
                        />
                      </Grid.Column>
                  }
                  {
                      inkassoWarning &&
                      <Grid.Column width={8}>
                        <div className="inkasso-warn">
                          <Icon name="warning sign" color="brown"/>
                          {getMessage("patientCentral.checkIn.warning.inkassoWarning")}
                        </div>
                      </Grid.Column>
                  }
                </Grid.Row>
              </>
          }

          <Grid.Row>
            <Grid.Column width={3}>
              <label htmlFor="urgency">{getMessage("patientCentral.checkIn.urgency")}:</label>
            </Grid.Column>
            <Grid.Column width={8}>
              <Field
                  id="urgency"
                  name="urgency"
                  component={Select}
                  validate={required}
                  options={urgencyOptions}
                  disabled={true}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={3}>
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  id="generateCertificate"
                  name="generateCertificate"
                  component={CheckBox}
                  label={getMessage("patientCentral.checkIn.generateCertificate")}
                  disabled={true}
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  id="generateReport"
                  name="generateReport"
                  component={CheckBox}
                  label={getMessage("patientCentral.checkIn.generateReport")}
                  disabled={true}
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={3}>
              <label htmlFor="remarks">{getMessage("anamnesis.form.treatment.remarks")}:</label>
            </Grid.Column>
            <Grid.Column width={8}>
              <Field
                  id="remarks"
                  name="remarks"
                  component={TextArea}
                  rows={2}
              />
            </Grid.Column>
          </Grid.Row>

        </PxGrid>
      </div>

      <div className="actions-row">
        <Button type="button"
                compact
                primary
                onClick={(evt) => handleSubmit(evt as any)}
                disabled={submitting}
        >
          {getMessage("button.save")}
        </Button>

        <Button type="button"
                compact
                onClick={this.props.onCancel}
                disabled={submitting}
        >
          {getMessage("button.cancel")}
        </Button>

        {
            this.state.submitError &&
            <div className="submit-error">
              {getMessage(this.state.submitError)}
            </div>
        }

        {
            this.state.submitWarning &&
            <div className="submit-warn">
              {getMessage(this.state.submitWarning)}
            </div>
        }
      </div>

      <FormSpy
          subscription={{values: true, hasValidationErrors: true}}
          onChange={this.onFormChange}
      />
    </CheckInOutDiv>;
  }

  renderUpdateFromHealthCardPopupContent = () => {
    const {getMessage} = this.props;
    const {healthCardPopupStatus} = this.state;

    let content = () => {
      switch (healthCardPopupStatus) {
        case CardReaderState.INITIAL :
          return <InfoDiv>
            <div className="info-message minimal-info-message">
              {getMessage('patient.searchFromHealthCardPopup.state.initial')}
            </div>
          </InfoDiv>;

        case CardReaderState.NO_EXTENSION :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.NO_EXTENSION')}
              <div className="download-link">
                <a href="https://chrome.google.com/webstore/detail/iggeppehjnmjeeicldkpjopdofglljei?authuser=0&hl=en"
                   target="_blank">{getMessage('nativeTools.download.chromeExtension')}</a>
              </div>
            </div>
          </InfoDiv>;

        case CardReaderState.OUTDATED_EXTENSION :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.OUTDATED_EXTENSION')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.chromeExtension')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case CardReaderState.NO_HOST_APPLICATION :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.NO_HOST_APPLICATION')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.hostApplication')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case CardReaderState.OUTDATED_HOST_APP :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.OUTDATED_HOST_APP')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.hostApplication')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case CardReaderState.NO_CARD :
          return <InfoDiv>
            <div className="info-message minimal-info-message">
              {getMessage('patient.searchFromHealthCardPopup.state.noCard')}
            </div>
          </InfoDiv>;
      }
    }

    return (
        <div>
          {content()}
          <div>
            <Button type="button" compact onClick={this.closeUpdateFromHealthCardPopup}>
              {getMessage("button.close")}
            </Button>
          </div>
        </div>
    )
  };

  vaccinationCardErrorPopupContent = () => {
    const {getMessage} = this.props;
    const {vaccinationError} = this.state;

    if (!vaccinationError) {
      return <></>;
    }

    return <PopupErrorMessage
        negative
        icon="warning sign"
        header={getMessage(vaccinationError!.errorKey)}
        list={vaccinationError!.fields.map(field => getMessage(`patient.vaccinationCard.error.field.${field}`))}
    />;
  }

  renderUpsertPatient = (): React.ReactNode => {

    const {patientModalOpen, patient, patientId, countries, languages} = this.state;
    const {currentMandates, getAllMandates} = this.props;

    return (
        <>
          {patientModalOpen &&
              <UpsertPatient
                  modalOpen={patientModalOpen}
                  toggleModal={this.togglePatientModal}
                  patient={patient}
                  patientId={patientId}
                  countries={countries}
                  languages={languages}
                  currentMandates={currentMandates}
                  getAllMandates={getAllMandates}
                  onPatientSave={this.onPatientSave}
              />
          }
        </>
    );
  };

  openPatientModal = (patientId: number) => {

    this.setState({patientLoading: true});
    findPatientById(this.cancelTokenSource, patientId)
        .then(response => {
          this.setState({
            patientId: patientId,
            patient: response
          });
          this.togglePatientModal();
        })
        .catch(noop)
        .finally(() => this.setState({patientLoading: false}));
  };

  onPatientSave = async (patient: PatientDto) => {
    const {bookingInTransition} = this.props;
    const patientData: PatientDataResponseDto = await getPatientData(bookingInTransition.patientId, this.cancelTokenSource)

    this.setState({
      patientData: patientData
    });
    this.togglePatientModal();
  }

  togglePatientModal = () => {
    this.setState(({patientModalOpen}: State) => ({
      patientModalOpen: !patientModalOpen
    }));
  };

  onSaveDoctor = () => {
    const {doctorId} = this.state;
    if (doctorId) {
      this.fetchSpecificReferralDoctor(doctorId);
    }
    this.hideDoctorModal();
  };

  openEditDoctorModal = (id: number | undefined) => {
    if (id) {
      findDoctorById(id, this.cancelTokenSource).then(response => {
        this.showDoctorModal(id, response);
      }).catch(noop);
    }
  };

  showDoctorModal = (id?: number, doctor?: UpsertDoctorDto) => {

    this.setState(({}: State) => {
      return {
        editDoctorModalOpen: true,
        doctor: doctor,
        doctorId: id
      }
    });
  };

  hideDoctorModal = () => {

    this.setState(({}: State) => ({
      editDoctorModalOpen: false,
      doctorId: undefined,
      doctor: undefined
    }));
  };

  renderUpsertDoctorModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {editDoctorModalOpen, doctor, doctorId} = this.state;

    return (
        <Modal
            open={editDoctorModalOpen}
            onClose={() => this.hideDoctorModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("doctors")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertDoctorForm
                doctor={doctor}
                doctorId={doctorId}
                onSave={this.onSaveDoctor}
                onCancel={this.hideDoctorModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

}

export default withMessages(
    withAuthContext(
        withPatientCentralRefDataContext(
            withPatientCentralContext(
                withMandateContext(VaccinationTransitionForm)))));
